// Read models and RPCs. Everything the user writes goes through the outbox (events) or an
// RPC that itself inserts an event; the client never mutates a read model.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { enqueue } from "./outbox";
import type {
  AdventureEntry,
  Board,
  DailySummary,
  Exercise,
  NewEvent,
  Profile,
  Skill,
  SkillState,
  Streak,
  UnlockProgress,
  XpAward,
} from "./types";

async function unwrap<T>(
  p: PromiseLike<{ data: T | null; error: { message: string } | null }>,
): Promise<T> {
  const { data, error } = await p;
  if (error) throw new Error(error.message);
  return data as T;
}

export const keys = {
  board: ["board"] as const,
  skills: ["skills"] as const,
  skillState: ["skill_state"] as const,
  exercises: ["exercises"] as const,
  profile: ["profile"] as const,
  streak: ["streak"] as const,
  summary: (days: number) => ["summary", days] as const,
  adventure: ["adventure"] as const,
  unlocks: ["unlocks"] as const,
  awards: (skill: string) => ["awards", skill] as const,
  sessionEvents: (sessionId: string) => ["session", sessionId] as const,
};

export function useProfile() {
  return useQuery({
    queryKey: keys.profile,
    queryFn: () => unwrap<Profile>(supabase.from("profiles").select("*").single()),
    staleTime: 5 * 60_000,
  });
}

export function useSkills() {
  return useQuery({
    queryKey: keys.skills,
    queryFn: () => unwrap<Skill[]>(supabase.from("skills").select("*").order("sort")),
    staleTime: Infinity,
  });
}

export function useSkillState() {
  return useQuery({
    queryKey: keys.skillState,
    queryFn: () =>
      unwrap<SkillState[]>(
        supabase.from("skill_state").select("skill_id, xp, level, unlocked, unlocked_at"),
      ),
  });
}

export function useExercises() {
  return useQuery({
    queryKey: keys.exercises,
    queryFn: () =>
      unwrap<Exercise[]>(supabase.from("exercises").select("*").eq("active", true).order("name")),
    staleTime: Infinity,
  });
}

export function useBoard() {
  return useQuery({
    queryKey: keys.board,
    queryFn: () => unwrap<Board>(supabase.rpc("ensure_today")),
    refetchInterval: 60_000,
  });
}

export function useStreak() {
  return useQuery({
    queryKey: keys.streak,
    queryFn: () =>
      unwrap<Streak>(supabase.from("streaks").select("current, longest, last_date").single()),
  });
}

export function useSummary(days = 14) {
  return useQuery({
    queryKey: keys.summary(days),
    queryFn: () =>
      unwrap<DailySummary[]>(
        supabase
          .from("daily_summary")
          .select("*")
          .order("local_date", { ascending: false })
          .limit(days),
      ),
  });
}

export function useUnlocks() {
  return useQuery({
    queryKey: keys.unlocks,
    queryFn: () => unwrap<UnlockProgress[]>(supabase.rpc("unlock_progress")),
  });
}

export function useAdventure() {
  return useQuery({
    queryKey: keys.adventure,
    queryFn: () =>
      unwrap<AdventureEntry[]>(
        supabase.from("adventure_log").select("*").order("at", { ascending: false }).limit(60),
      ),
  });
}

export interface AwardWithEvent extends XpAward {
  events: { type: string; occurred_at: string; payload: Record<string, unknown> } | null;
}

export function useAwards(skill: string) {
  return useQuery({
    queryKey: keys.awards(skill),
    queryFn: () =>
      unwrap<AwardWithEvent[]>(
        supabase
          .from("xp_awards")
          .select("event_id, skill_id, xp, local_date, events(type, occurred_at, payload)")
          .eq("skill_id", skill)
          .order("local_date", { ascending: false })
          .limit(200)
          .returns<AwardWithEvent[]>(),
      ),
    enabled: Boolean(skill),
  });
}

/** Everything derived from the ledger changes when an event lands. */
export function useInvalidateDerived() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.board });
    void qc.invalidateQueries({ queryKey: keys.skillState });
    void qc.invalidateQueries({ queryKey: keys.streak });
    void qc.invalidateQueries({ queryKey: ["summary"] });
    void qc.invalidateQueries({ queryKey: keys.adventure });
    void qc.invalidateQueries({ queryKey: keys.unlocks });
    void qc.invalidateQueries({ queryKey: ["awards"] });
  };
}

export function useLogEvent(userId: string) {
  const invalidate = useInvalidateDerived();
  return useMutation({
    mutationFn: async (events: NewEvent | NewEvent[]) => {
      const list = Array.isArray(events) ? events : [events];
      const ids: string[] = [];
      for (const e of list) ids.push(await enqueue(userId, e));
      return ids;
    },
    onSuccess: () => {
      // The outbox flushes async; refetch shortly after so the board and XP catch up.
      window.setTimeout(invalidate, 900);
      window.setTimeout(invalidate, 3500);
    },
  });
}

export function useCompleteQuest() {
  const invalidate = useInvalidateDerived();
  return useMutation({
    mutationFn: (instanceId: string) =>
      unwrap<unknown>(supabase.rpc("complete_quest", { p_instance: instanceId })),
    onSuccess: invalidate,
  });
}

export function useRerollQuest() {
  const invalidate = useInvalidateDerived();
  return useMutation({
    mutationFn: (instanceId: string) =>
      unwrap<unknown>(supabase.rpc("reroll_quest", { p_instance: instanceId })),
    onSuccess: invalidate,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Profile>) =>
      unwrap<Profile>(supabase.from("profiles").update(patch).select().single()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.profile }),
  });
}
